<template>
  <div class="content">
    <div v-if="!record.id">
      <div class="btn-container u-fill-width u-text-right">
        <back-button></back-button>
      </div>
      <placeholder
        v-if="!error.serverError"
        title="Loading record"
      ></placeholder>
      <server-error v-bind:error="error.serverError"></server-error>
    </div>
    <div v-else>
      <!-- Navigation button -->
      <div class="btn-container u-pull-right u-text-right">
        <router-link
          :to="{ name: 'record-file', params: { id: record.candidate.id } }"
          class="action-button"
        >
          <button class="btn-dark action-button">
            <i
              class="fa-wrapper fa fa-folder-open normal-icon pad-right"
              aria-hidden="true"
            ></i>
            File
          </button>
        </router-link>
        <a @click="$router.go(-1)" class="action-button">
          <button class="btn-info action-button">
            <i
              class="fa-wrapper fa fa-reply normal-icon pad-right"
              aria-hidden="true"
            ></i>
            Back
          </button>
        </a>
      </div>
      <h2 class="font-light">
        {{ record.task_code }}: {{ record.task_name }}
        <record-repeat-icon
          v-bind:isRepeat="record.is_repeat"
        ></record-repeat-icon>
      </h2>
      <record-info v-if="record.candidate" v-bind:record="record"></record-info>
      <!-- Error and success message -->
      <toast-success
        v-if="success"
        label="Record Submitted"
        message="The record has been closed and saved on the server."
        v-on:close="closeSuccess"
      ></toast-success>
      <server-error v-bind:error="error.serverError"></server-error>
      <!-- Signature fields -->
      <div v-if="showCandidateSignature() || showInstructorSignature()">
        <div class="divider"></div>
        <h2 class="font-light">Certification</h2>
        <signature-form
          v-if="showCandidateSignature()"
          v-bind:user="record.candidate"
          v-bind:role="'Candidate'"
          v-bind:error="error.candidate_signature"
          v-on:sign="signRecord"
        ></signature-form>
        <div
          v-if="showCandidateSignature() && showInstructorSignature()"
          class="divider"
        ></div>
        <signature-form
          v-if="showInstructorSignature()"
          v-bind:user="record.instructor"
          v-bind:role="'Instructor'"
          v-bind:error="error.instructor_signature"
          v-on:sign="signRecord"
        ></signature-form>
        <div class="divider"></div>
      </div>
      <!-- Record Content.
           This changes based on whether the record is being viewed or is being
           completed. -->
      <placeholder-no-results
        v-if="recordSwitch() == 'hide'"
        title="Record Not Available"
        message="Sorry, the details for this record aren't available for you to view."
      ></placeholder-no-results>
      <record-view
        v-if="recordSwitch() == 'view'"
        v-bind:record="record"
        v-bind:items="items"
      ></record-view>
      <record-complete
        v-if="recordSwitch() == 'complete'"
        v-bind:record="record"
        v-bind:items="items"
      ></record-complete>
      <div v-if="items.length > 0" class="section u-box">
        <div class="row">
          <div class="col-12">
            <h6 class="font-alt font-light">Assessment Markers</h6>
            <div
              class="row u-box u-no-margin u-vertical-center"
              style="border-radius: 0;"
              v-for="marker in markers"
              v-bind:key="`marker-decode-${marker.code}`"
            >
              <div class="col-1 offset-1">
                <div class="tag" v-bind:class="getMarkerStatus(marker)">
                  <strong>{{ marker.code }}</strong>
                </div>
              </div>
              <div class="col-2">
                {{ marker.name }}
              </div>
              <div class="col-8">
                <small>{{ marker.description }}</small>
              </div>
            </div>
            <div class="divider"></div>
            <h6 class="font-alt font-light">Reason Codes</h6>
            <div class="row">
              <div
                v-for="reason in reasons"
                v-bind:key="`reason-decode-${reason.code}`"
                v-bind:class="`col-${12 / reasons.length}`"
                class="u-text-center"
              >
                <div class="tag">
                  <strong>{{ reason.code }}</strong>
                </div>
                <space></space>
                <small>{{ reason.name }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import SignatureForm from "@/components/SignatureForm";
import BackButton from "@/components/BackButton";
import ServerError from "@/components/ServerError";
import ToastSuccess from "@/components/ToastSuccess";
import Placeholder from "@/components/Placeholder";
import PlaceholderNoResults from "@/components/PlaceholderNoResults";
import { scrollToTop } from "@/functions/scrollToTop";
// Record components and functions
import RecordInfo from "@/components/records/RecordInfo";
import RecordRepeatIcon from "@/components/records/RecordRepeatIcon";
import RecordView from "@/components/records/RecordView";
import RecordComplete from "@/components/records/RecordComplete";
import cleanReasonCode from "@/functions/cleanReasonCode.function";
import { getRecordMarkerStatus } from "@/functions/getRecordMarkerStatus";

export default {
  name: "record-detail",
  components: {
    SignatureForm,
    BackButton,
    ServerError,
    ToastSuccess,
    Placeholder,
    PlaceholderNoResults,
    RecordInfo,
    RecordRepeatIcon,
    RecordView,
    RecordComplete
  },
  data: function() {
    return {
      success: false
    };
  },
  computed: {
    record: function() {
      var result = this.records[this.$route.params.id];
      if (!result) {
        return {};
      } else {
        return result;
      }
    },
    items: function() {
      let stack = this.$store.getters["records/items"](this.record.id);
      if (stack) {
        for (let item of stack) {
          item = cleanReasonCode(item);
        }
        return stack;
      } else {
        return [];
      }
    },
    ...mapGetters("records", ["records", "markers", "reasons"]),
    ...mapGetters("core", ["error"]),
    ...mapGetters("auth", ["userProfile", "permissions"])
  },
  watch: {
    "record.id": function() {
      // Get the items for the record whenever the record changes
      this.$store.dispatch("records/getItems", {
        id: this.$route.params.id,
        isOnline: this.isOnline
      });
    }
  },
  methods: {
    recordSwitch() {
      // Logic to determine if view is hide/complete/sign/view etc.
      if (!this.record.id || !this.userProfile.id) {
        return "hide";
      } else if (
        !this.record.is_closed &&
        this.record.instructor.id == this.userProfile.id
      ) {
        // Instructor then may complete record.
        return "complete";
      } else {
        // May view record if authorised or involved with the record.
        let is_involved =
          this.userProfile.id == this.record.instructor.id ||
          this.userProfile.id == this.record.candidate.id;
        let view_permission = this.permissions.includes("records.view_record");
        if (is_involved || view_permission) {
          return "view";
        } else {
          return "hide";
        }
      }
    },
    closeSuccess: function() {
      this.success = false;
    },
    signRecord(role, code) {
      this.success = false;
      let field = role.toLowerCase() + "_signature";
      this.$store.commit("core/clearErrors");
      // Handle empty fields
      if (code == "") {
        let err = {};
        err[field] = "You must enter your signature code.";
        this.$store.commit("core/setError", err);
        return;
      }
      this.$store.dispatch("records/signRecord", {
        id: this.record.id,
        username: this.record[role.toLowerCase()].email,
        signature: code,
        role: role.toLowerCase() + "_signature"
      });
    },
    showCandidateSignature: function() {
      if (
        !this.record ||
        this.record.status < 0 ||
        !this.record.candidate ||
        !this.record.instructor
      ) {
        return false;
      }
      const isInvolved =
        this.record.candidate.id == this.userProfile.id ||
        this.record.instructor.id == this.userProfile.id;
      if (!isInvolved) {
        return false;
      } else {
        return (
          this.record.is_closed && this.record["candidate_signature"] == null
        );
      }
    },
    showInstructorSignature: function() {
      if (
        !this.record ||
        this.record.status < 0 ||
        !this.record.candidate ||
        !this.record.instructor
      ) {
        return false;
      }
      // Only show if logged in as the intructor
      if (this.record.instructor.id != this.userProfile.id) {
        return false;
      } else {
        return (
          this.record.is_closed && this.record["instructor_signature"] == null
        );
      }
    },
    getMarkerStatus(marker) {
      return getRecordMarkerStatus(marker);
    }
  },
  mounted() {
    // Clear existing errors
    this.$store.commit("core/clearErrors");
    // Get the record
    let data = { id: this.$route.params.id, isOnline: this.isOnline };
    this.$store.dispatch("records/getRecord", data);
    this.$store.dispatch("records/getItems", data);
    scrollToTop();
  }
};
</script>
